import { cn } from '@/lib/cn';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';

interface PaginationProps {
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  layout?: 'row' | 'column';
}

export function Pagination({
  currentPage,
  lastPage,
  total,
  perPage,
  onPageChange,
  isLoading,
  layout = 'row',
}: PaginationProps) {
  if (lastPage <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const showMax = 5;

    let start = Math.max(1, currentPage - Math.floor(showMax / 2));
    const end = Math.min(lastPage, start + showMax - 1);

    if (end === lastPage) {
      start = Math.max(1, end - showMax + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const isColumn = layout === 'column';
  return (
    <div
      className={cn(
        'flex w-full items-center py-4',
        isColumn
          ? 'flex-col-reverse gap-3'
          : 'flex-col-reverse justify-between md:flex-row',
      )}
    >
      <div className="text-muted-foreground text-sm">
        Showing {(currentPage - 1) * perPage + 1} to{' '}
        {Math.min(currentPage * perPage, total)} of {total} results
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outlinePrimary"
          size="icon"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
          className="hover:bg-muted size-10 border-none hover:shadow-none"
        >
          <ChevronLeft className="size-5" />
        </Button>

        <div className="flex items-center gap-1">
          {getPageNumbers().map((page) => (
            <Button
              key={page}
              variant={currentPage === page ? 'primary' : 'ghost'}
              size="icon"
              onClick={() => onPageChange(page)}
              disabled={isLoading}
              className={cn(
                'size-10 rounded-lg text-sm font-medium shadow-none hover:shadow-sm',
                currentPage === page ? '' : 'hover:bg-muted',
              )}
            >
              {page}
            </Button>
          ))}
        </div>

        <Button
          variant="outlinePrimary"
          size="icon"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === lastPage || isLoading}
          className="hover:bg-muted size-10 border-none hover:shadow-none"
        >
          <ChevronRight className="size-5" />
        </Button>
      </div>
    </div>
  );
}
